import { useEffect, useRef, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { BINGO_MUSIC_MAX_BYTES, type BingoMusicView } from '../../../shared/bingo.js';
import { measure } from './AutoBingoAdmin.js';
import { mp3sFromZip } from './zipRead.js';

// The waiting music of the automatic bingo (user request 2026-10-03): MP3 files in their playing order.
// With the switch in the settings on, they play on the player page from 30 minutes before the draw.

type Flash = { ok: boolean; text: string } | null;
const m = () => t.admin.bingoMusic;
const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];
const length = (ms: number) => `${Math.floor(ms / 60000)}.${String(Math.round((ms % 60000) / 1000)).padStart(2, '0')}`;
/** The music level (raised 2026-10-03): × WAIT_VOLUME 0.6 in sound.ts ≈ −26 LUFS, about the speech (−28). */
const MUSIC_LEVEL_DB = -22;

export function BingoMusic(props: { onFlash: (f: Flash) => void }) {
  const [tracks, setTracks] = useState<BingoMusicView[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<BingoMusicView | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const folder = useRef<HTMLInputElement>(null);
  const zip = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  useEffect(() => {
    api<{ tracks: BingoMusicView[] }>('GET', '/api/admin/bingo/music')
      .then((r) => setTracks(r.tracks))
      .catch((err) => props.onFlash({ ok: false, text: errorText(err) }));
  }, []);

  /** Single files, a whole folder or a ZIP (unpacked here; user request 2026-10-03) – uploaded one by one. */
  const upload = async (source: 'files' | 'folder' | 'zip') => {
    const input = source === 'files' ? file.current : source === 'folder' ? folder.current : zip.current;
    const picked = [...(input?.files ?? [])];
    if (picked.length === 0) return;
    setBusy(true);
    let added = 0;
    let failed = 0;
    try {
      let files: File[];
      if (source === 'zip') {
        files = [];
        for (const z of picked) files.push(...(await mp3sFromZip(z).catch(() => Promise.reject(new Error('zip')))));
      } else files = picked.filter((x) => /\.mp3$/i.test(x.name)).sort((a, b) => a.name.localeCompare(b.name, 'fi', { numeric: true }));
      if (files.length === 0) return props.onFlash({ ok: false, text: m().noMp3 });
      setProgress({ done: 0, total: files.length });
      for (const [i, f] of files.entries()) {
        setProgress({ done: i, total: files.length });
        if (f.size > BINGO_MUSIC_MAX_BYTES) {
          failed++;
          continue;
        }
        const measured = await measure(f, MUSIC_LEVEL_DB).catch(() => null);
        if (!measured) {
          failed++;
          continue;
        }
        const name = f.name.replace(/\.mp3$/i, '').slice(0, 80);
        const q = new URLSearchParams({ name, ms: String(measured.ms), volume: measured.volume.toFixed(3) });
        const r = await fetch(`/api/admin/bingo/music?${q}`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'audio/mpeg' }, body: f });
        const data = (await r.json().catch(() => ({}))) as { tracks?: BingoMusicView[]; error?: string };
        if (!r.ok || !data.tracks) throw new ApiError((data.error as never) ?? 'internal_error', r.status);
        setTracks(data.tracks);
        added++;
      }
      props.onFlash({ ok: failed === 0, text: m().addedCount(added, failed) });
    } catch (err) {
      props.onFlash({ ok: false, text: err instanceof Error && err.message === 'zip' ? m().zipBad : errorText(err) });
    } finally {
      setBusy(false);
      setProgress(null);
      if (input) input.value = '';
    }
  };
  const run = async (fn: () => Promise<{ tracks: BingoMusicView[] }>) => {
    setBusy(true);
    try {
      setTracks((await fn()).tracks);
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  const total = (tracks ?? []).reduce((s, x) => s + x.ms, 0);

  return (
    <section class="card form">
      <h2>{m().title}</h2>
      <p class="hint">{m().info}</p>
      {!tracks ? (
        <p class="muted">{t.admin.autoBingo.loading}</p>
      ) : tracks.length === 0 ? (
        <p class="muted">{m().none}</p>
      ) : (
        <>
          <p class="muted">{m().total(tracks.length, length(total))}</p>
          <ol class="music-list">
            {tracks.map((x, i) => (
              <li key={x.id}>
                <span class="music-name">{x.name}</span>
                <span class="muted">
                  {length(x.ms)} · {(x.bytes / 1024 / 1024).toFixed(1)} Mt
                </span>
                <span class="music-actions">
                  <button class="btn btn-small" type="button" disabled={busy || i === 0} title={m().up} onClick={() => void run(() => api('POST', `/api/admin/bingo/music/${x.id}/move`, { dir: -1 }))}>
                    ↑
                  </button>
                  <button
                    class="btn btn-small"
                    type="button"
                    disabled={busy || i === tracks.length - 1}
                    title={m().down}
                    onClick={() => void run(() => api('POST', `/api/admin/bingo/music/${x.id}/move`, { dir: 1 }))}
                  >
                    ↓
                  </button>
                  <button class="btn btn-small btn-danger" type="button" disabled={busy} onClick={() => setRemoving(x)}>
                    {t.admin.bingo.remove}
                  </button>
                </span>
              </li>
            ))}
          </ol>
        </>
      )}
      <label>
        {m().upload}
        <input ref={file} type="file" accept=".mp3,audio/mpeg" multiple disabled={busy} onChange={() => void upload('files')} />
        <small>{m().uploadHint}</small>
      </label>
      <div class="bingo-start-actions">
        <label class="btn btn-small">
          📁 {m().uploadFolder}
          <input ref={folder} type="file" hidden disabled={busy} {...{ webkitdirectory: '' }} multiple onChange={() => void upload('folder')} />
        </label>
        <label class="btn btn-small">
          🗜 {m().uploadZip}
          <input ref={zip} type="file" hidden accept=".zip,application/zip" disabled={busy} onChange={() => void upload('zip')} />
        </label>
      </div>
      {progress ? <p class="muted">{m().progress(progress.done, progress.total)}</p> : busy && <p class="muted">{m().working}</p>}
      {removing && (
        <ConfirmDialog
          title={m().removeTitle(removing.name)}
          confirmLabel={t.admin.bingo.remove}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={() => {
            const x = removing;
            setRemoving(null);
            void run(() => api('DELETE', `/api/admin/bingo/music/${x.id}`));
          }}
          onCancel={() => setRemoving(null)}
        >
          <p>{m().removeText}</p>
        </ConfirmDialog>
      )}
    </section>
  );
}

/** The audio stream (2026-10-03): an https address (e.g. Icecast) for the waiting and background music. */
export function StreamSettings(props: { onFlash: (f: Flash) => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<{ url: string }>('GET', '/api/admin/bingo/stream')
      .then((r) => setUrl(r.url))
      .catch((err) => props.onFlash({ ok: false, text: errorText(err) }));
  }, []);
  const save = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    try {
      setUrl((await api<{ url: string }>('PUT', '/api/admin/bingo/stream', { url: (url ?? '').trim() })).url);
      props.onFlash({ ok: true, text: m().streamSaved });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form class="card form" onSubmit={save}>
      <h2>{m().streamTitle}</h2>
      <p class="hint">{m().streamInfo}</p>
      <label>
        {m().streamUrl}
        <input type="url" value={url ?? ''} disabled={url === null} placeholder="https://…" onInput={(e) => setUrl(e.currentTarget.value)} />
      </label>
      {url && (
        <audio controls preload="none" src={url}>
          {m().streamTest}
        </audio>
      )}
      <button class="btn btn-primary" type="submit" disabled={busy || url === null}>
        {m().streamSave}
      </button>
    </form>
  );
}
